import * as React from "react";
import { FC, useEffect } from "react";
import { Button } from "~/components/Button";
import { InfoPopover } from "~/components/InfoPopover";
import { useModals } from "~/components/Modal";
import { Progress } from "~/components/Progress";
import style from "~/components/ResourcePage/index.module.scss";
import { Table } from "~/components/Table";
import { Title } from "~/components/Title";
import { useAbility } from "~/lib/ability";
import { useDataSource } from "~/lib/dataSource";
import { Intent } from "~/lib/intent";
import { ClientsAbility } from "~/modules/Clients/ability";
import { SellerFinanceAdapter } from "~/modules/Clients/adapters";
import { fetchSellerAccountInfo, forcePayments, forceReleasePayments, updateFinanceTransaction } from "~/modules/Clients/api";
import { renderDollars } from "~/lib/helpers";
import { stringDateFormat, dateFromString } from "~/lib/date";
import { BalanceForm } from "~/modules/Sources/forms/FinanceForm";
import { onSubmitError } from "~/components/Form";
import { buildDefaultBalance } from "../models/Finance";
import { getGranularityDateFormat } from "~/modules/Dashboard/lib/performance";
import { Granularity } from "~/modules/Dashboard/models/Performance";
import { useResource } from "~/lib/loader";
import { ForceReleaseForm } from "~/modules/Sources/forms/ForceReleaseForm";
import { RequestPaymentForm } from "~/modules/Sources/forms/RequestPaymentForm";
import { createPayment } from "~/modules/Payments/api";
import { ForcePayForm } from "~/modules/Sources/forms/ForcePayForm";
import { getReleaseIntervalStatusLabel } from "~/modules/Clients/models/AccountInfo";
import localStyle from "./SellerFinance.module.scss";

interface ISellerFinanceParams {
  sellerId: string;
  sellerName: string;
  viewOnly: boolean;
}

export const SellerFinance: FC<ISellerFinanceParams> = (props) => {
  const { state, fetchRecord } = useDataSource(SellerFinanceAdapter);
  const ability = useAbility(ClientsAbility);
  const dailyDateFormat = getGranularityDateFormat(Granularity.DAILY);
  const {
    resource: accountInfo,
    isPending: accountInfoLoading,
    fetch: refetchAccountInfo,
  } = useResource(() => fetchSellerAccountInfo(props.sellerId));

  const scheduledPayout = state.records.reduce((acc, record) => acc + record.scheduledPayout, 0);

  const modal = useModals();

  const hasPendingInterval = React.useMemo(() => {
    const finance = state.records[0];
    const details = finance?.intervalInfo ?? [];
    return details.some((item) => item.status === "pending");
  }, [state.records]);

  const editBalance = modal.create(BalanceForm, {
    sellerName: props.sellerName,
    paymentReleaseDate: state.records[0] ? dateFromString(state.records[0].nextReleaseDate) : new Date(),
    onSubmit: (balance, formikActions, close) => {
      updateFinanceTransaction(Number(props.sellerId), balance).then(close).then(fetchFinance).catch(onSubmitError(formikActions));
    },
  });

  const pendingIntervals = React.useMemo(
    () => (accountInfo?.lastUnreleasedIntervals ?? []).filter((interval) => interval.status === "pending"),
    [accountInfo?.lastUnreleasedIntervals]
  );

  const forceRelease = modal.create(ForceReleaseForm, {
    sellerName: props.sellerName,
    onSubmit: (formikActions, close) => {
      forceReleasePayments(props.sellerId).then(close).then(fetchFinance).catch(onSubmitError(formikActions));
    },
    unreleasedIntervals: pendingIntervals,
  });

  const forcePay = modal.create(ForcePayForm, {
    sellerName: props.sellerName,
    scheduledPayout,
    onSubmit: (formikActions, close) => {
      forcePayments(props.sellerId).then(close).then(fetchFinance).catch(onSubmitError(formikActions));
    },
  });

  const intervalToRequests = React.useMemo(
    () => (accountInfo?.lastUnreleasedIntervals ?? []).filter((interval) => !["created", "pending"].includes(interval.status)),
    [accountInfo?.lastUnreleasedIntervals]
  );

  const requestPayments = modal.create(RequestPaymentForm, {
    sellerName: props.sellerName,
    onSubmit: (formikActions, close) => {
      createPayment(props.sellerId).then(close).then(fetchFinance).catch(onSubmitError(formikActions));
    },
    intervals: intervalToRequests,
  });

  const fetchFinance = () => {
    fetchRecord({ sellerId: props.sellerId });
    refetchAccountInfo();
  };

  useEffect(fetchFinance, []);

  return (
    <>
      <Title
        className={style.titleMt}
        toolbar={
          <>
            {!props.viewOnly && ability.canCreateAgency() && (
              <Button
                intent={Intent.DANGER}
                responsive
                leftIcon={<i className="fa fa-money" />}
                disabled={state.isPending || accountInfoLoading || accountInfo.minPaymentsBalance > scheduledPayout}
                onClick={() => {
                  forcePay.show();
                }}
              >
                Force pay
              </Button>
            )}
            {!props.viewOnly && ability.canCreateSource() && (
              <Button
                intent={Intent.DANGER}
                responsive
                leftIcon={<i className="fa fa-money" />}
                disabled={state.isPending || accountInfoLoading || !pendingIntervals.length}
                onClick={() => {
                  forceRelease.show();
                }}
              >
                Force release
              </Button>
            )}
            {!props.viewOnly && ability.canCreateSource() && (
              <Button
                intent={Intent.PRIMARY}
                responsive
                leftIcon={<i className="fa fa-edit" />}
                disabled={state.isPending || accountInfoLoading || !intervalToRequests.length}
                onClick={() => {
                  requestPayments.show();
                }}
              >
                Request payments
              </Button>
            )}
            {!props.viewOnly && ability.canCreateSource() && (
              <Button
                intent={Intent.PRIMARY}
                responsive
                key="create"
                leftIcon={<i className="fa fa-edit" />}
                disabled={state.isPending}
                onClick={() =>
                  editBalance.show({
                    balance: buildDefaultBalance(state.records[0]),
                  })
                }
              >
                Edit Balance
              </Button>
            )}

            <Button
              intent={Intent.SUCCESS}
              responsive
              key="refresh"
              disabled={state.isPending}
              leftIcon={<i className="fa fa-refresh" />}
              onClick={fetchFinance}
            >
              Refresh
            </Button>
          </>
        }
      >
        <span className={localStyle.titleInline}>
          <span className={localStyle.titleText}>Finance</span>
          {hasPendingInterval && (
            <span className={localStyle.pendingBadge}>
              {getReleaseIntervalStatusLabel("pending")}
            </span>
          )}
        </span>
      </Title>
      <Table
        header
        columns={[
          {
            field: "scheduledPayout",
            header: "Scheduled Payout",
            small: true,
            hide: props.viewOnly,
            cell: (finance) => renderDollars(finance.scheduledPayout),
          },
          {
            field: "nextReleaseDate",
            header: "Upcoming Release Date",
            small: true,
            hide: props.viewOnly,
            cell: (finance) => stringDateFormat(finance.nextReleaseDate, dailyDateFormat),
          },
          {
            field: "interval",
            header: "Payment Interval",
            small: true,
            hide: props.viewOnly,
            cell: (finance) => {
              const intervals = Array.isArray(finance.interval)
                ? finance.interval
                : finance.interval
                  ? [finance.interval]
                  : [];
              if (!intervals.length) {
                return "—";
              }

              const intervalLabels = intervals.map(
                (interval) =>
                  `${stringDateFormat(interval.start, dailyDateFormat)} - ${stringDateFormat(interval.end, dailyDateFormat)}`
              );
              const intervalSummary =
                intervalLabels.length > 1 ? `${intervalLabels[0]} +${intervalLabels.length - 1} more` : intervalLabels[0];
              const details = finance.intervalInfo ?? [];
              const hasPendingStatus = details.some((item) => item.status === "pending");
              const hasPopover = !hasPendingStatus && (details.length > 0 || intervalLabels.length > 1);

              const summary = (
                <span className={localStyle.intervalSummary}>{intervalSummary}</span>
              );

              if (!hasPopover) {
                return summary;
              }

              const popoverContent = (
                <div className={localStyle.intervalPopoverContent}>
                  {intervalLabels.length > 1 && (
                    <div className={localStyle.intervalPopoverSection}>
                      <div className={localStyle.intervalPopoverTitle}>Intervals</div>
                      <div className={localStyle.intervalPopoverList}>
                        {intervalLabels.map((label, idx) => (
                          <div key={`${idx}-${label}`} className={localStyle.intervalPopoverNumeric}>
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {details.length > 0 && (
                    <div className={localStyle.intervalPopoverSection}>
                      <div className={localStyle.intervalPopoverTitle}>Interval details</div>
                      <div className={localStyle.intervalPopoverList}>
                        {details.map((item, index) => {
                          return (
                            <div
                              key={`${item.releaseId}-${item.status}-${index}`}
                              className={localStyle.intervalPopoverRow}
                            >
                              <span className={localStyle.intervalPopoverNumeric}>{item.releaseId}</span>
                              <span className={localStyle.intervalPopoverValue}>
                                {item.status === "not_faced_threshold" &&
                                typeof accountInfo?.minPaymentsBalance === "number"
                                  ? `${getReleaseIntervalStatusLabel(item.status)} ${renderDollars(
                                      accountInfo.minPaymentsBalance
                                    )}`
                                  : getReleaseIntervalStatusLabel(item.status)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );

              return (
                <InfoPopover ariaLabel="Interval details" width={360} content={popoverContent}>
                  {summary}
                </InfoPopover>
              );
            },
          },
          {
            field: "currentBalance",
            header: "Current Balance",
            small: true,
            hide: props.viewOnly,
            cell: (finance) => renderDollars(finance.currentBalance),
          },
        ]}
        records={state.records}
        placeholder={state.isPending && <Progress />}
      />
    </>
  );
};
